import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { isSameDay } from 'date-fns';
import { DialogContent, Stack, Tooltip, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import SessionCreateDialogActions from 'components/session/dialog/create/SessionCreateDialogActions';
import SessionBatchConfirmDialog from 'components/session/dialog/create/SessionBatchConfirmDialog';
import MultiDatePanel from 'components/session/dialog/create/MultiDatePanel';
import SessionFieldsListeners from 'components/session/fields/SessionFieldsListeners';
import SessionFields from 'components/session/fields/SessionFields';
import { createSessionInitialValues } from 'common/form/initial-values/create';
import StyledSessionDialogCreateForm from 'common/theme/dialog/create-session/StyledSessionDialogCreateForm';
import defaultSessionFormValidations from 'common/validations/form/defaultSession';
import { formDecorator } from 'common/utils/session';
import { isClient } from 'common/utils/user';
import useCreateSession from 'hooks/session/useCreateSession';

const calculator = formDecorator;

const dayKey = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

function SessionCreateDialogContent({ onDialogClose }) {
  const formRef = useRef(null);
  const { t } = useTranslation(['labels', 'sessions', 'pqrs']);
  const { createSession } = useCreateSession();

  // Fechas extra del HORARIO ACTIVO (el que edita el formulario).
  const [extraDates, setExtraDates] = useState([]);
  // Horarios CONGELADOS: [{ id, fromHour, toHour, trainerId, trainerName, dates: [Date] }].
  const [blocks, setBlocks] = useState([]);
  // Panel multi-fecha plegable (abierto por defecto): al plegarlo el form se expande.
  const [panelOpen, setPanelOpen] = useState(true);
  // Transición de limpieza al agregar otro horario (loading dorado).
  const [transitioning, setTransitioning] = useState(false);
  // Confirmación de lote (varias sesiones).
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingValuesRef = useRef(null);

  const user = useSelector((state) => state.auth.user);
  const trainers = useSelector((state) => state.trainer.data.trainers);
  const isClientUser = isClient(user.role);

  const initialValues = useMemo(
    () =>
      isClientUser
        ? { ...createSessionInitialValues(), client_id: { label: user.full_name, value: user.id } }
        : createSessionInitialValues(),
    [user.role]
  );

  const trainerNameById = useCallback(
    (id) => trainers?.find((tr) => String(tr.id) === String(id))?.full_name || null,
    [trainers]
  );

  const toggleExtraDate = useCallback((date, primaryDate) => {
    // La fecha primaria (la del formulario) no se togglea como adicional.
    if (primaryDate && isSameDay(date, primaryDate)) return;
    setExtraDates((prev) =>
      prev.some((d) => isSameDay(d, date))
        ? prev.filter((d) => !isSameDay(d, date))
        : [...prev, date]
    );
  }, []);

  // Snapshot del horario ACTIVO a partir del formulario (null si está incompleto).
  const snapshotActive = useCallback((values) => {
    const fromHour = values.from instanceof Date ? values.from.getHours() : null;
    let toHour = null;
    if (values.to instanceof Date) toHour = values.to.getHours();
    else if (fromHour !== null) toHour = fromHour + 1;
    const primaryDate = values.date instanceof Date ? values.date : null;
    if (fromHour === null || !values.trainer_id || !primaryDate) return null;

    const dates = [primaryDate, ...extraDates.filter((d) => !isSameDay(d, primaryDate))];
    return {
      id: Date.now(),
      fromHour,
      toHour,
      trainerId: values.trainer_id,
      trainerName: trainerNameById(values.trainer_id),
      dates,
    };
  }, [extraDates, trainerNameById]);

  // "Agregar otro horario": congela el activo y LIMPIA el formulario (un solo sistema,
  // sin dropdowns en las filas) con un loading dorado durante la transición.
  const handleAddBlock = useCallback(() => {
    const form = formRef.current;
    const values = form?.getState()?.values || {};
    const snapshot = snapshotActive(values);
    if (!snapshot || !form) return;

    setTransitioning(true);
    setBlocks((prev) => [...prev, snapshot]);
    setExtraDates([]);
    form.batch(() => {
      form.change('date', null);
      form.change('from', '');
      form.change('to', '');
      form.change('trainer_id', '');
    });
    // El listener de 'date' va con debounce y vuelve a limpiar hora/entrenador:
    // mantenemos el loading hasta que el formulario queda estable.
    setTimeout(() => setTransitioning(false), 700);
  }, [snapshotActive]);

  const handleRemoveBlock = useCallback((id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const handleRemoveBlockDate = useCallback((id, date) => {
    setBlocks((prev) =>
      prev
        .map((b) => (b.id === id ? { ...b, dates: b.dates.filter((d) => !isSameDay(d, date)) } : b))
        .filter((b) => b.dates.length > 0)
    );
  }, []);

  // Lista completa de sesiones a crear: horarios congelados + horario activo (form).
  const buildSessions = useCallback(
    (values) => {
      const sessions = [];
      const seen = new Set();
      const push = (date, fromHour, toHour, trainerId, trainerName) => {
        const key = `${dayKey(date)}-${fromHour}`;
        if (seen.has(key)) return; // nunca dos sesiones del cliente el mismo día+hora
        seen.add(key);
        sessions.push({ date, fromHour, toHour, trainerId, trainerName });
      };

      blocks.forEach((b) => {
        b.dates.forEach((d) => push(d, b.fromHour, b.toHour, b.trainerId, b.trainerName));
      });

      const fromHour = values.from instanceof Date ? values.from.getHours() : null;
      let toHour = null;
      if (values.to instanceof Date) toHour = values.to.getHours();
      else if (fromHour !== null) toHour = fromHour + 1;
      const primaryDate = values.date instanceof Date ? values.date : null;
      if (fromHour !== null && values.trainer_id && primaryDate) {
        push(primaryDate, fromHour, toHour, values.trainer_id, trainerNameById(values.trainer_id));
        extraDates.forEach((d) => push(d, fromHour, toHour, values.trainer_id, trainerNameById(values.trainer_id)));
      }

      return sessions;
    },
    [blocks, extraDates, trainerNameById]
  );

  const doCreate = useCallback(
    (values) => {
      createSession(values, user, buildSessions(values));
      setExtraDates([]);
      setBlocks([]);
      setConfirmOpen(false);
      onDialogClose();
    },
    [createSession, user, buildSessions, onDialogClose]
  );

  const onCreate = (values) => {
    const sessions = buildSessions(values);
    // Varias sesiones → modal de confirmación con la lista (Confirmar / Continuar editando).
    if (sessions.length > 1) {
      pendingValuesRef.current = values;
      setConfirmOpen(true);
      return;
    }
    doCreate(values);
  };

  return (
    <DialogContent>
      {isClientUser && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2, p: 1.25, borderRadius: '12px', border: '1px solid', borderColor: 'hairline.main', backgroundColor: 'surface.raised' }}
        >
          <Tooltip title={t('helpHint', { ns: 'pqrs' })} arrow>
            <InfoOutlined sx={{ color: 'gold.main', fontSize: 20, flexShrink: 0 }} />
          </Tooltip>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('carryOverHint', { ns: 'pqrs', defaultValue: 'Si te quedan 4 sesiones o menos y tu plan vence, las sumamos a tu próximo pago al reactivarlo (hasta un máximo de 4 sesiones).' })}
          </Typography>
        </Stack>
      )}
      <Form
        decorators={[calculator]}
        initialValues={initialValues}
        onSubmit={onCreate}
        validate={defaultSessionFormValidations}
        render={({ handleSubmit, form, invalid, pristine, values }) => {
          formRef.current = form;
          const primaryDate = values.date instanceof Date ? values.date : null;
          const fromHour = values.from instanceof Date ? values.from.getHours() : null;
          const toHour = values.to instanceof Date ? values.to.getHours() : null;

          // Gate del panel multi-fecha: admin necesita cliente; cliente necesita plan;
          // ambos necesitan categoría + idioma (hora y ENTRENADOR se exigen después).
          const hasSubject = isClientUser ? Boolean(values.payment_id) : Boolean(values.client_id?.value);
          const panelDisabled = !hasSubject || !values.session_type_id || !values.language;

          let disabledReason = null;
          if (!hasSubject) {
            disabledReason = isClientUser
              ? t('multiDateNeedsPlan', { ns: 'sessions', defaultValue: 'Selecciona primero tu plan activo.' })
              : t('multiDateNeedsClient', { ns: 'sessions', defaultValue: 'Selecciona primero el cliente.' });
          } else if (!values.session_type_id || !values.language) {
            disabledReason = t('multiDateNeedsDetails', {
              ns: 'sessions',
              defaultValue: 'Completa categoría e idioma para habilitar el calendario.',
            });
          }

          // Fechas ya agendadas a la MISMA hora en horarios congelados: deshabilitadas
          // en el calendario (el cliente no puede estar en dos sesiones a la vez).
          const takenDates = new Set(
            blocks
              .filter((b) => b.fromHour === fromHour)
              .flatMap((b) => b.dates.map((d) => dayKey(d)))
          );

          const canAddBlock = Boolean(fromHour !== null && values.trainer_id && primaryDate);

          const sessionsPreview = confirmOpen && pendingValuesRef.current
            ? buildSessions(pendingValuesRef.current)
            : [];

          return (
            <StyledSessionDialogCreateForm onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid xs={12} lg={panelOpen ? 7 : 11}>
                  <SessionFields values={values} />
                </Grid>
                <Grid xs={12} lg={panelOpen ? 5 : 1}>
                  <MultiDatePanel
                    open={panelOpen}
                    onToggleOpen={() => setPanelOpen((o) => !o)}
                    disabled={panelDisabled}
                    disabledReason={disabledReason}
                    primaryDate={primaryDate}
                    primaryFromHour={fromHour}
                    primaryToHour={toHour}
                    trainerId={values.trainer_id}
                    trainerName={trainerNameById(values.trainer_id)}
                    sessionTypeId={values.session_type_id}
                    language={values.language}
                    extraDates={extraDates}
                    onTogglePrimaryBlockDate={(d) => toggleExtraDate(d, primaryDate)}
                    blocks={blocks}
                    onRemoveBlock={handleRemoveBlock}
                    onRemoveBlockDate={handleRemoveBlockDate}
                    onAddBlock={handleAddBlock}
                    canAddBlock={canAddBlock}
                    transitioning={transitioning}
                    takenDates={takenDates}
                  />
                </Grid>
              </Grid>
              <SessionCreateDialogActions
                // Con horarios CONGELADOS se puede crear aunque el horario activo esté
                // vacío (p. ej. agregaste otro horario y cambiaste de idea): las
                // sesiones salen de los congelados; el activo incompleto se omite.
                invalid={invalid && blocks.length === 0}
                onDialogClose={onDialogClose}
                pristine={pristine && extraDates.length === 0 && blocks.length === 0}
              />
              <SessionFieldsListeners form={formRef?.current} />

              <SessionBatchConfirmDialog
                open={confirmOpen}
                sessions={sessionsPreview}
                onConfirm={() => doCreate(pendingValuesRef.current)}
                onClose={() => setConfirmOpen(false)}
              />
            </StyledSessionDialogCreateForm>
          );
        }}
      />
    </DialogContent>
  );
}

SessionCreateDialogContent.propTypes = {
  onDialogClose: PropTypes.func.isRequired
};

export default SessionCreateDialogContent;
